import React, { useCallback, useEffect, useRef, useState } from 'react';
import { auth } from '../../../lib/firebase';
import { createAssessmentShare } from '../../../lib/assessmentData';
import { useAuth } from '../../../contexts/AuthContext';
import CosmicIcon from '../../common/CosmicIcon';
import './CosmicShareButton.css';

const SHARE_ID_KEY = 'cosmic-share-id';

// A share create must never hang the button forever (the 2026-08 spinner bug).
// Race the write against a timeout so a stuck write surfaces as a retryable error.
const withTimeout = (promise, ms, label) => Promise.race([
  promise,
  new Promise((_, reject) => setTimeout(() => reject(new Error(`${label || 'operation'} timed out`)), ms)),
]);

// Pull a friendly first name for the share snapshot from auth or the saved
// registration blob. Never falls back to anything PII-ish; empty is fine.
function deriveFirstName(explicit) {
  if (explicit && explicit.trim()) return explicit.trim().split(/\s+/)[0];
  try {
    const fromAuth = auth.currentUser?.displayName;
    if (fromAuth) return fromAuth.trim().split(/\s+/)[0];
    const reg = JSON.parse(localStorage.getItem('cosmic-registration-data') || '{}');
    if (reg.name) return String(reg.name).trim().split(/\s+/)[0];
  } catch (_) { /* no-op */ }
  return '';
}

// Pre-written, on-voice share copy for each channel. Names the constellation
// when we have it and always carries the invite link. Kept plain and warm,
// never shilly (public-facing-voice).
function buildShareMessages(constellationName, link) {
  const named = constellationName && constellationName.trim();
  const sms = named
    ? `My career constellation came out as the ${named}. Here's mine, and you can map yours free here: ${link}`
    : `I just mapped my career constellation. Here's mine, and you can map yours free here: ${link}`;
  const emailSubject = 'My career constellation';
  const emailBody = named
    ? `I took this career assessment and it mapped me as the ${named}. Here's my result: ${link}\n\nIf you want, map yours free and we can compare.`
    : `I took this career assessment and mapped my career constellation. Here's my result: ${link}\n\nIf you want, map yours free and we can compare.`;
  const linkedIn = named
    ? `I mapped my career constellation and came out as the ${named}. It goes after what pulls you toward work, not only what you are good at. Here's mine, and you can map yours free: ${link}`
    : `I mapped my career constellation. It goes after what pulls you toward work, not only what you are good at. Here's mine, and you can map yours free: ${link}`;
  return { sms, emailSubject, emailBody, linkedIn };
}

/**
 * CosmicShareButton - pinned to the results top nav. Creates (or refreshes) a
 * public, sanitized snapshot of the taker's constellation and hands back a
 * copyable link that anyone can open at /r/:shareId, plus one-click channel
 * shares (text, email, LinkedIn) pre-filled with natural copy.
 */
const CosmicShareButton = ({ results, tier, firstName }) => {
  const { signInWithGoogle } = useAuth();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | creating | ready | needauth | error
  const [link, setLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [liCopied, setLiCopied] = useState(false);
  const popRef = useRef(null);
  const inputRef = useRef(null);
  const copyTimer = useRef(null);
  const liTimer = useRef(null);

  const buildLink = (id) => `${window.location.origin}/r/${id}`;

  const ensureShare = useCallback(async () => {
    if (!auth.currentUser) {
      setStatus('needauth');
      return;
    }
    if (status === 'creating') return;
    setStatus('creating');
    const ownerUid = auth.currentUser.uid;
    const name = deriveFirstName(firstName);
    const stored = localStorage.getItem(SHARE_ID_KEY) || undefined;
    try {
      let id;
      try {
        id = await withTimeout(
          createAssessmentShare({ ownerUid, firstName: name, results, tier, shareId: stored }),
          15000, 'share create',
        );
      } catch (reuseErr) {
        // Stored token may belong to another account on this device, be unwritable,
        // or the write may have stalled — mint a fresh one (also time-bounded).
        if (stored) {
          id = await withTimeout(
            createAssessmentShare({ ownerUid, firstName: name, results, tier }),
            15000, 'share create',
          );
        } else {
          throw reuseErr;
        }
      }
      localStorage.setItem(SHARE_ID_KEY, id);
      setLink(buildLink(id));
      setStatus('ready');
    } catch (err) {
      console.error('Failed to create share link:', err);
      setStatus('error');
    }
  }, [results, tier, firstName, status]);

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !link) ensureShare();
  };

  const handleSignIn = async () => {
    try {
      await signInWithGoogle();
      ensureShare();
    } catch (err) {
      console.error('Share sign-in failed:', err);
      setStatus('error');
    }
  };

  const handleCopy = async () => {
    if (!link) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(link);
      } else if (inputRef.current) {
        inputRef.current.select();
        document.execCommand('copy');
      }
      setCopied(true);
      clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 2200);
    } catch (err) {
      // Last resort: select the field so the user can copy manually.
      if (inputRef.current) inputRef.current.select();
    }
  };

  // LinkedIn drops any pre-filled caption from the share URL, so copy the
  // suggested caption to the clipboard and open the composer with the link
  // (its OG card). The visitor pastes the caption into their post.
  const handleLinkedIn = async (caption, url) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(caption);
        setLiCopied(true);
        clearTimeout(liTimer.current);
        liTimer.current = setTimeout(() => setLiCopied(false), 4000);
      }
    } catch (_) { /* clipboard optional; still open the composer */ }
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => () => {
    clearTimeout(copyTimer.current);
    clearTimeout(liTimer.current);
  }, []);

  return (
    <div className="cosmic-share" ref={popRef}>
      <button
        type="button"
        className={`cosmic-share-btn ${open ? 'cosmic-share-btn--active' : ''}`}
        onClick={handleToggle}
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Share your constellation"
      >
        <span className="cosmic-share-btn__label">Share<span className="cosmic-share-btn__label-extra"> my results</span></span>
      </button>

      {open && (
        <div className="cosmic-share-pop" role="dialog" aria-label="Share your constellation">
          <div className="cosmic-share-pop__arrow" aria-hidden="true" />
          <h4 className="cosmic-share-pop__title">Share your constellation</h4>

          {status === 'needauth' && (
            <>
              <p className="cosmic-share-pop__text">
                Sign in to create a link you can share with anyone.
              </p>
              <button type="button" className="cosmic-btn cosmic-btn--primary cosmic-btn--full cosmic-btn--sm" onClick={handleSignIn}>
                Sign in to get your link
              </button>
            </>
          )}

          {status === 'creating' && (
            <p className="cosmic-share-pop__text cosmic-share-pop__text--muted">
              <span className="cosmic-share-spinner" /> Creating your link…
            </p>
          )}

          {status === 'error' && (
            <>
              <p className="cosmic-share-pop__text cosmic-share-pop__text--error">
                Couldn't create the link. Please try again.
              </p>
              <button type="button" className="cosmic-btn cosmic-btn--secondary cosmic-btn--full cosmic-btn--sm" onClick={ensureShare}>
                Retry
              </button>
            </>
          )}

          {status === 'ready' && (
            <>
              <p className="cosmic-share-pop__text cosmic-share-pop__text--muted">
                Anyone with this link can view your results. No email or contact info is shown.
              </p>
              <p className="cosmic-share-pop__gift">
                <CosmicIcon name="ui_gift" size={15} decorative />
                Whoever you send it to can map their own, free.
              </p>
              <div className="cosmic-share-pop__row">
                <input
                  ref={inputRef}
                  className="cosmic-share-pop__input"
                  value={link}
                  readOnly
                  onFocus={(e) => e.target.select()}
                  aria-label="Shareable link"
                />
                <button
                  type="button"
                  className={`cosmic-share-pop__copy ${copied ? 'is-copied' : ''}`}
                  onClick={handleCopy}
                >
                  <CosmicIcon name={copied ? 'ui_check' : 'ui_link'} size={16} mono decorative />
                  {copied ? 'Copied!' : 'Copy link'}
                </button>
              </div>

              {(() => {
                const msg = buildShareMessages(results?.constellationData?.name, link);
                return (
                  <>
                    <p className="cosmic-share-pop__or">Or send it straight to someone</p>
                    <div className="cosmic-share-pop__channels">
                      <a
                        className="cosmic-share-chan"
                        href={`sms:?&body=${encodeURIComponent(msg.sms)}`}
                      >
                        <CosmicIcon name="ui_phone" size={16} mono decorative />
                        <span>Text</span>
                      </a>
                      <a
                        className="cosmic-share-chan"
                        href={`mailto:?subject=${encodeURIComponent(msg.emailSubject)}&body=${encodeURIComponent(msg.emailBody)}`}
                      >
                        <CosmicIcon name="ui_mail" size={16} mono decorative />
                        <span>Email</span>
                      </a>
                      <button
                        type="button"
                        className={`cosmic-share-chan ${liCopied ? 'is-copied' : ''}`}
                        onClick={() => handleLinkedIn(msg.linkedIn, link)}
                      >
                        <CosmicIcon name={liCopied ? 'ui_check' : 'ui_globe'} size={16} mono decorative />
                        <span>LinkedIn</span>
                      </button>
                    </div>
                    {liCopied && (
                      <p className="cosmic-share-pop__text cosmic-share-pop__text--muted cosmic-share-pop__li-note">
                        Caption copied. Paste it into your LinkedIn post.
                      </p>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CosmicShareButton;
